import {
  ArrowUpDown,
  BarChart3,
  Columns3,
  LayoutDashboard,
  PhoneOff,
  ClipboardList,
  FilePlus2,
  Users,
  CalendarDays,
  Camera,
  CircleHelp,
  Clock,
  Coffee,
  KeyRound,
  ListChecks,
  LogIn,
  Route,
  Smartphone,
  type LucideIcon,
} from 'lucide-react'

export type GuideTopic = {
  slug: string
  title: string
  icon: LucideIcon
  /** Only ready topics are links; the rest render as a muted "Bald" card. */
  ready: boolean
}

export type GuidePhase = {
  title: string
  topics: GuideTopic[]
}

export const LOGIN_SLUG = 'anmelden'
export const TERMIN_ABLAUF_SLUG = 'termin-ablauf'
export const INSTALL_APP_SLUG = 'app-installieren'
export const KALENDER_ANSICHT_SLUG = 'kalender-ansicht'
export const TERMIN_PLANEN_SLUG = 'termin-planen'
export const HANDWERKER_NACH_GEWERK_SLUG = 'handwerker-nach-gewerk'
export const NICHT_ERREICHT_SLUG = 'nicht-erreicht'
export const PLANUNGSBEDARF_SLUG = 'planungsbedarf'
export const REIHENFOLGE_HANDWERKER_SLUG = 'reihenfolge-handwerker'
export const DASHBOARD_SLUG = 'dashboard'

export const GUIDE_ROLES = ['handwerker', 'buero', 'leiter'] as const
export type GuideRole = (typeof GUIDE_ROLES)[number]

export const GUIDE_ROLE_LABELS: Record<GuideRole, string> = {
  handwerker: 'Handwerker',
  buero: 'Büromitarbeiter',
  leiter: 'Leiter',
}

export const GUIDE_PHASES: Record<GuideRole, GuidePhase[]> = {
  handwerker: [
    {
      title: 'Erste Schritte',
      topics: [
        { slug: LOGIN_SLUG, title: 'Anmelden', icon: LogIn, ready: true },
        { slug: INSTALL_APP_SLUG, title: 'App installieren & Benachrichtigungen', icon: Smartphone, ready: true },
      ],
    },
    {
      title: 'Arbeitstag',
      topics: [
        { slug: KALENDER_ANSICHT_SLUG, title: 'Ein Tag oder drei Tage', icon: Columns3, ready: true },
        { slug: 'kalender', title: 'Mein Kalender', icon: CalendarDays, ready: false },
        { slug: TERMIN_ABLAUF_SLUG, title: 'Termin-Ablauf', icon: Route, ready: true },
        { slug: 'aufgaben', title: 'Aufgaben erledigen', icon: ListChecks, ready: false },
        { slug: 'pausen', title: 'Pausen', icon: Coffee, ready: false },
      ],
    },
    {
      title: 'Rückmeldung & Zeiten',
      topics: [
        { slug: 'rueckmeldung', title: 'Kommentar und Fotos ans Büro', icon: Camera, ready: false },
        { slug: 'privatzeit', title: 'Privatzeit eintragen', icon: Clock, ready: false },
      ],
    },
    {
      title: 'Hilfe',
      topics: [
        { slug: 'passwort-vergessen', title: 'Passwort vergessen', icon: KeyRound, ready: false },
        { slug: 'probleme', title: 'Häufige Probleme', icon: CircleHelp, ready: false },
      ],
    },
  ],
  buero: [
    {
      title: 'Aufträge',
      topics: [
        { slug: 'auftrag-anlegen', title: 'Reparaturauftrag anlegen', icon: FilePlus2, ready: false },
        { slug: 'auftrag-verfolgen', title: 'Aufträge verfolgen', icon: ClipboardList, ready: false },
        { slug: NICHT_ERREICHT_SLUG, title: 'Mieter nicht erreicht', icon: PhoneOff, ready: true },
      ],
    },
    {
      title: 'Planung',
      topics: [
        { slug: TERMIN_PLANEN_SLUG, title: 'Termin planen und speichern', icon: CalendarDays, ready: true },
        { slug: HANDWERKER_NACH_GEWERK_SLUG, title: 'Handwerker nach Gewerk', icon: Users, ready: true },
        { slug: 'aufgaben-planen', title: 'Aufgaben planen', icon: ListChecks, ready: false },
        { slug: PLANUNGSBEDARF_SLUG, title: 'Planungsbedarf', icon: Route, ready: true },
      ],
    },
    {
      title: 'Auswertung',
      topics: [
        { slug: 'rueckmeldungen-lesen', title: 'Rückmeldungen der Handwerker', icon: Camera, ready: false },
        { slug: 'controlling', title: 'Controlling', icon: BarChart3, ready: false },
      ],
    },
  ],
  leiter: [
    {
      title: 'Überblick',
      topics: [{ slug: DASHBOARD_SLUG, title: 'Dashboard', icon: LayoutDashboard, ready: true }],
    },
    {
      title: 'Team',
      topics: [
        { slug: REIHENFOLGE_HANDWERKER_SLUG, title: 'Reihenfolge der Handwerker', icon: ArrowUpDown, ready: true },
        { slug: 'benutzer-verwalten', title: 'Benutzer verwalten', icon: Users, ready: false },
        { slug: 'zeitmanagement', title: 'Abwesenheiten und Zeiten', icon: Clock, ready: false },
      ],
    },
    {
      title: 'Auswertung',
      topics: [{ slug: 'arbeitszeit-report', title: 'Arbeitszeit-Report', icon: BarChart3, ready: false }],
    },
  ],
}

export const findTopic = (slug: string | undefined): GuideTopic | undefined =>
  Object.values(GUIDE_PHASES)
    .flat()
    .flatMap((p) => p.topics).find((t) => t.slug === slug)
